"use client";

import { Link } from "@/i18n/navigation";
import { PhoneIcon, ArrowRightIcon, CheckCircleIcon } from "@heroicons/react/24/outline";
import Contact from "@/components/Contact/Contact";

const PHONE_TEL = "+32493966507";

const CONTENT = {
  fr: {
    badge: "Guide",
    h1: "Vide maison à Bruxelles : le guide complet pour organiser votre projet",
    heroSubtitle:
      "Que vous prépariez une succession, une vente ou simplement le besoin de faire place nette, voici comment se déroule un vide maison à Bruxelles, de la première estimation à la remise des clés.",
    ctaCall: "Appeler maintenant",
    ctaQuote: "Demander un devis gratuit",
    intro:
      "Un vide maison à Bruxelles consiste à évacuer entièrement le contenu d'un bien — meubles, encombrants, cave, grenier, garage — avant une vente, une location ou dans le cadre d'une succession. Contrairement à un simple débarras ponctuel, il inclut généralement le tri des objets, la valorisation de ceux qui ont une réelle valeur marchande, et la remise du bien dans un état propre et libre. Ce guide détaille les étapes concrètes d'un vide maison, les facteurs qui influencent le prix et le délai, la manière dont les objets sont triés et évacués, ainsi que les points d'accès et de préparation à anticiper selon votre commune. Vous y trouverez aussi les réponses aux questions les plus fréquentes posées par les Bruxellois avant de faire appel à une entreprise spécialisée.",
    s1: {
      title: "Qu'est-ce qu'un vide maison, exactement ?",
      p1: "Un vide maison est une opération plus large qu'un simple débarras : il ne s'agit pas seulement d'évacuer des encombrants, mais de traiter l'intégralité d'un bien, pièce par pièce, en distinguant ce qui doit être conservé, ce qui peut être racheté, et ce qui part en évacuation. L'objectif final est de rendre le bien entièrement libre — pas nécessairement rénové ou fraîchement nettoyé de fond en comble, mais débarrassé de tout contenu et prêt pour l'étape suivante : une vente, une mise en location ou une remise des clés au syndic ou au propriétaire.",
      p2Before: "Selon la configuration du bien, un vide maison peut se limiter à un ",
      p2Link1: "appartement",
      p2Mid: " ou couvrir une ",
      p2Link2: "maison complète",
      p2Mid2: " avec ses annexes. Certains espaces demandent une attention particulière, notamment les ",
      p2Link3: "caves, greniers et garages",
      p2After: ", souvent plus longs à traiter que les pièces de vie.",
      p3: "Un vide maison ne comprend en revanche pas les gros travaux de rénovation ni un nettoyage professionnel en profondeur (traces au sol, peinture, désinfection) : il s'agit d'évacuer le contenu, pas de remettre le bien à neuf. Si un nettoyage plus poussé est nécessaire après l'évacuation, il fait généralement l'objet d'une prestation distincte, à clarifier dès le devis.",
    },
    s2: {
      title: "Dans quelles situations un vide maison est-il utile ?",
      pBefore: "La demande la plus fréquente concerne les ",
      pLink: "successions",
      pAfter: ", lorsqu'un logement doit être libéré avant une vente ou un partage entre héritiers. Mais d'autres situations reviennent régulièrement :",
      items: [
        "La mise en vente ou en location d'un bien, pour le présenter vide aux visiteurs ou aux futurs locataires",
        "Le départ d'un proche vers une maison de repos, quand le logement doit être rendu ou repris",
        "La fin d'un bail, avec l'obligation de restituer un bien vidé et en état",
        "Un désencombrement avant travaux ou rénovation",
        "Une séparation ou un déménagement impliquant de vider rapidement un logement",
      ],
      p2: "Chaque situation a ses propres contraintes de délai et de sensibilité — une succession se traite rarement avec la même urgence qu'une fin de bail. Ces situations partagent toutefois un point commun : le bien doit être vidé dans un délai déterminé, avec des enjeux différents selon le contexte. Une succession implique souvent un tri plus long, le temps d'identifier les objets à valeur sentimentale ; une fin de bail impose en revanche généralement un délai plus serré, fixé par le contrat de location.",
    },
    s3: {
      title: "Comment se déroule un vide maison, étape par étape",
      intro: "Le déroulement suit globalement toujours la même logique, quelle que soit la taille du bien : un premier contact, une estimation, un devis, l'intervention, puis la remise du bien. Voici ce que chaque étape implique concrètement.",
      steps: [
        ["Premier contact", "Vous décrivez votre situation par téléphone ou via le formulaire en ligne : type de bien, volume approximatif, délai souhaité, présence éventuelle d'objets de valeur."],
        ["Estimation", "Nous évaluons le volume, l'accès et les objets présents, sur place ou à partir de photos et d'une description détaillée."],
        ["Devis gratuit", "Vous recevez un devis clair et détaillé, incluant la déduction des objets rachetés le cas échéant, sans engagement de votre part."],
        ["Intervention", "L'équipe évacue le bien intégralement, à la date convenue, en respectant les modalités d'accès fixées à l'avance."],
        ["Bien libéré", "Le bien est vidé et prêt à être remis : vente, location, remise des clés au syndic ou au propriétaire."],
      ],
      h3a: "L'estimation : sur place ou à distance",
      pa: "Pour un petit volume ou une situation simple, quelques photos et une description suffisent souvent à établir un devis fiable. Pour un bien plus vaste, avec plusieurs pièces, une cave et un grenier, une visite sur place permet d'évaluer plus précisément le volume réel et les contraintes d'accès, et d'éviter les mauvaises surprises le jour de l'intervention.",
      h3b: "Le jour de l'intervention",
      pb: "L'équipe procède pièce par pièce : les objets déjà identifiés comme rachetés sont mis de côté, le reste est évacué vers les filières adaptées. Pour un studio, l'intervention peut se terminer en une matinée ; pour une maison avec dépendances, elle peut s'étaler sur une à deux journées selon le volume et le nombre de personnes mobilisées.",
    },
    s4: {
      title: "Combien de temps et quel prix prévoir ?",
      p1Before: "Il n'existe pas de tarif fixe : le prix d'un vide maison dépend du volume à évacuer, de l'accès au bien (étages, ascenseur, stationnement) et de la présence d'objets de valeur qui viennent réduire la facture. Notre page ",
      p1Link1: "tarifs",
      p1Mid: " détaille les facteurs qui entrent en jeu, et notre article sur ",
      p1Link2: "le coût d'un vide maison à Bruxelles",
      p1After: " va plus loin sur ce sujet précis. Dans tous les cas, le devis est gratuit et sans engagement.",
      p2: "Le prix final dépend principalement de :",
      items: [
        "Le volume réel à évacuer, pièce par pièce",
        "L'accès au bien : étage, présence d'un ascenseur, possibilité de se garer à proximité",
        "Le nombre d'espaces annexes à traiter (cave, grenier, garage) en plus du logement lui-même",
        "La valeur des objets rachetés, qui vient directement réduire le montant du devis",
        "Le degré d'urgence de la demande, qui peut influencer l'organisation de l'intervention",
      ],
      p3Before: "Un « vide maison entièrement gratuit » n'est réaliste que dans des cas très particuliers, généralement quand la valeur des objets rachetés couvre l'intégralité du coût de l'intervention — ce qui reste l'exception plutôt que la règle. Notre article ",
      p3Link: "vide maison gratuit, est-ce possible ?",
      p3After: " explique dans quels cas cette situation peut se présenter.",
    },
    ctaBox1: {
      title: "Une idée du volume à évacuer ?",
      text: "Demandez une estimation gratuite et sans engagement, sur place ou à distance selon votre situation.",
      call: "Appeler maintenant",
      quote: "Devis gratuit",
    },
    s5: {
      title: "Tri, évacuation et valorisation : que deviennent les objets ?",
      p1Before: "Avant toute évacuation, nous examinons ce qui a une valeur réelle : mobilier ancien, bijoux, argenterie, tableaux, collections. Ces objets sont proposés au rachat, et leur montant est déduit directement du devis — voir notre page ",
      p1Link1: "rachat d'objets de valeur",
      p1Mid: ". Pour savoir reconnaître ce qui vaut la peine d'être mis de côté avant notre passage, notre article ",
      p1Link2: "comment reconnaître un objet de valeur",
      p1After: " donne des repères concrets.",
      p2Before: "Le reste du contenu — mobilier sans valeur marchande, encombrants, déchets divers — est évacué vers les filières de tri et de recyclage adaptées, dans le respect des règles en vigueur à Bruxelles. Vous trouverez plus de détails sur ce circuit et ce qui est réellement récupéré ou recyclé dans notre article ",
      p2Link: "que deviennent les meubles après un débarras",
      p2After: ".",
      p3: "Le tri se fait généralement en deux temps : une première identification des objets à valeur lors de l'estimation, puis une vérification plus fine le jour de l'intervention, pièce par pièce, avant évacuation. Cette double vérification limite le risque qu'un objet intéressant parte par erreur avec le reste.",
    },
    s6: {
      title: "Accès, logistique et préparation : ce qu'il faut anticiper",
      p1: "À Bruxelles, l'accès au bien pèse souvent autant que le volume dans l'organisation d'un vide maison. Un appartement au quatrième étage sans ascenseur dans le centre-ville ne se traite pas comme une maison de plain-pied avec accès direct à la rue. Quelques points reviennent régulièrement : la présence ou non d'un ascenseur, la possibilité de se garer à proximité (fréquent sujet dans les communes au bâti dense), et l'accord du syndic lorsque le bien est en copropriété.",
      p2: "Le bâti bruxellois multiplie les configurations : maisons de maître avec plusieurs niveaux et une cave voûtée, immeubles à appartements avec cage d'escalier étroite, maisons mitoyennes accessibles uniquement depuis la rue. Chacune de ces configurations influence directement le temps de manutention et le nombre de personnes nécessaires sur place.",
      p3Before: "Les caves, greniers et garages demandent une préparation particulière : accès parfois restreint, objets entreposés depuis longtemps, présence possible d'éléments plus lourds ou volumineux. Notre article sur la ",
      p3Link: "durée d'un débarras de cave ou de grenier",
      p3After: " détaille ce qui influence le temps nécessaire pour ces espaces spécifiques.",
      p4Before: "Avant l'intervention, quelques gestes simples facilitent nettement le déroulement de la journée : mettre de côté les objets à conserver et les documents personnels, signaler les éventuelles restrictions d'accès, et prévoir un créneau horaire réaliste. Notre ",
      p4Link: "checklist avant un vide maison",
      p4After: " reprend l'ensemble de ces points de préparation.",
    },
    s7: {
      title: "Pourquoi faire appel à une entreprise spécialisée",
      p1: "Vider soi-même un bien entier demande du temps, un accès à un véhicule adapté, et une bonne connaissance des filières d'évacuation et de recyclage. Faire appel à une entreprise spécialisée permet de confier l'ensemble du processus à un seul interlocuteur : estimation, tri, valorisation des objets intéressants et évacuation, sans devoir coordonner soi-même plusieurs prestataires.",
      p2Before: "C'est aussi un moyen d'obtenir une évaluation objective des objets présents avant qu'ils ne soient évacués, plutôt que de devoir trancher seul entre ce qui a de la valeur et ce qui n'en a pas. Vous pouvez consulter des exemples concrets d'interventions sur notre page ",
      p2Link1: "réalisations",
      p2Mid: " et les retours de clients sur notre page ",
      p2Link2: "avis",
      p2After: ".",
      p3: "Cela ne dispense pas de rester attentif : demandez toujours un devis écrit avant l'intervention, clarifiez ce qui est inclus (évacuation seule ou également nettoyage sommaire), et n'hésitez pas à poser des questions sur la filière d'évacuation utilisée pour les déchets. Une entreprise sérieuse répond clairement à ces questions avant le jour J.",
    },
    retenir: {
      title: "À retenir",
      items: [
        "Un vide maison couvre l'ensemble d'un bien, avec tri et valorisation des objets — au-delà d'un simple débarras.",
        "Le prix dépend du volume, de l'accès et des objets rachetés : il n'y a pas de tarif fixe, mais le devis est toujours gratuit.",
        "Le tri distingue ce qui est racheté, ce qui est évacué et ce que vous conservez ; vous êtes informé avant toute proposition de rachat.",
        "L'accès (étages, ascenseur, stationnement, accord du syndic) influence autant l'organisation que le volume à évacuer.",
        "Les documents personnels et objets sentimentaux sont à mettre de côté vous-même avant l'intervention.",
      ],
    },
    s8: {
      title: "Bruxelles, commune par commune",
      pBefore: "Chaque commune bruxelloise a ses particularités — type de bâti, présence ou non d'ascenseurs, règles de stationnement, densité du quartier — qui influencent l'organisation d'un vide maison. Retrouvez les informations propres à votre secteur sur notre page ",
      pLink1: "vide maison à Bruxelles, commune par commune",
      pMid: ". Nous avons aussi détaillé certaines situations locales dans des guides dédiés, par exemple pour ",
      pLink2: "Ixelles",
      pSep1: ", ",
      pLink3: "Uccle",
      pSep2: ", ",
      pLink4: "Schaerbeek",
      pMid2: " ou ",
      pLink5: "Anderlecht",
      pAfter: ".",
    },
    faqTitle: "Questions fréquentes",
    faq: [
      {
        q: "Combien de temps prend un vide maison à Bruxelles ?",
        a: "Cela dépend surtout du volume et de la configuration du bien : un studio ou un appartement d'une chambre se vide généralement en quelques heures, tandis qu'une maison avec cave, grenier et jardin peut demander une à deux journées complètes. L'accès (étages, ascenseur, stationnement) et la présence d'objets à trier avec soin, par exemple lors d'une succession, peuvent aussi allonger l'intervention. Le temps nécessaire est estimé avec le devis, avant toute intervention.",
      },
      {
        q: "Faut-il être présent le jour de l'intervention ?",
        a: "Ce n'est pas une obligation si les modalités d'accès (clés, code, présence d'un syndic) sont convenues à l'avance et si les objets à conserver ont déjà été mis de côté. Cela dit, être présent, ne serait-ce qu'au début, permet de valider ensemble ce qui part et ce qui reste, et de répondre aux questions qui peuvent surgir en cours de journée.",
      },
      {
        q: "Le rachat d'objets de valeur est-il garanti ?",
        a: "Non : seuls les objets qui ont une réelle valeur marchande — mobilier ancien, bijoux, argenterie, œuvres, certaines collections — font l'objet d'une proposition de rachat. Ce n'est jamais systématique et cela dépend entièrement de ce qui se trouve dans le bien. Vous êtes toujours informé avant toute proposition et libre de l'accepter ou de la refuser ; le montant, s'il y en a un, est déduit du devis final.",
      },
      {
        q: "Que faire des papiers et documents personnels avant le passage de l'équipe ?",
        a: "Il est recommandé de rassembler vous-même les documents administratifs, financiers ou à valeur sentimentale (photos, courriers, carnets) avant l'intervention, car une équipe de débarras n'est pas en mesure de juger de leur importance. En cas de succession, ce tri préalable évite aussi de perdre un document utile aux démarches notariales.",
      },
      {
        q: "Un vide maison est-il possible sans place de parking juste devant l'immeuble ?",
        a: "Oui, mais il vaut mieux le signaler dès la demande de devis : l'équipe peut s'organiser en conséquence, par exemple avec un stationnement légèrement plus éloigné ou un créneau horaire adapté à la zone. Dans les communes au bâti dense du centre de Bruxelles, ce point est fréquent et se prépare simplement en amont.",
      },
      {
        q: "Quelle est la différence entre un vide maison et un débarras classique ?",
        a: "Un débarras se limite souvent à l'évacuation d'un espace ou d'un lot d'encombrants ponctuel. Un vide maison couvre l'ensemble d'un bien — pièces de vie, cave, grenier, garage — avec une étape de tri et de valorisation des objets intéressants, dans le but de remettre le logement entièrement libre, par exemple pour une vente ou une remise des clés.",
      },
      {
        q: "Peut-on demander un vide maison partiel, par exemple juste une cave ou un grenier ?",
        a: "Oui, une intervention peut se limiter à un seul espace si c'est votre besoin réel — une cave à vider avant des travaux, un grenier avant la vente d'un bien. Le principe reste le même (estimation, devis, tri, évacuation), seul le volume concerné change, ce qui se reflète directement dans le prix final.",
      },
    ],
    ctaBox2: {
      title: "Prêt à obtenir votre devis ?",
      text: "Contactez-nous pour une estimation gratuite et sans engagement.",
      call: "Appeler maintenant",
      quote: "Devis gratuit",
    },
  },
  nl: {
    badge: "Gids",
    h1: "Huis leegmaken in Brussel: de volledige gids om uw project te organiseren",
    heroSubtitle:
      "Of u nu een nalatenschap, een verkoop of gewoon de nood aan een opgeruimde woning voorbereidt: hier leest u hoe een huis leegmaken in Brussel verloopt, van de eerste schatting tot de sleuteloverdracht.",
    ctaCall: "Bel nu",
    ctaQuote: "Vraag een gratis offerte aan",
    intro:
      "Een huis leegmaken in Brussel bestaat erin de volledige inhoud van een pand te ontruimen — meubels, grofvuil, kelder, zolder, garage — vóór een verkoop, een verhuring of in het kader van een nalatenschap. In tegenstelling tot een eenmalige ontruiming omvat dit doorgaans het sorteren van voorwerpen, de valorisatie van wat een echte marktwaarde heeft, en de oplevering van het pand in een propere en volledig vrije staat. Deze gids beschrijft de concrete stappen van een huis leegmaken, de factoren die de prijs en de termijn beïnvloeden, de manier waarop voorwerpen gesorteerd en afgevoerd worden, en de toegangs- en voorbereidingspunten waarmee u rekening moet houden afhankelijk van uw gemeente. U vindt er ook antwoorden op de meest gestelde vragen van Brusselaars vóór ze een gespecialiseerd bedrijf inschakelen.",
    s1: {
      title: "Wat is een huis leegmaken precies?",
      p1: "Een huis leegmaken is een ruimere ingreep dan een gewone ontruiming: het gaat niet alleen om het afvoeren van grofvuil, maar om de behandeling van het volledige pand, kamer per kamer, waarbij een onderscheid wordt gemaakt tussen wat bewaard moet blijven, wat kan worden ingekocht en wat wordt afgevoerd. Het uiteindelijke doel is het pand volledig vrij te maken — niet noodzakelijk gerenoveerd of grondig gereinigd, maar ontdaan van alle inhoud en klaar voor de volgende stap: een verkoop, een verhuring of een sleuteloverdracht aan de syndicus of de eigenaar.",
      p2Before: "Afhankelijk van de configuratie van het pand kan een huis leegmaken beperkt blijven tot een ",
      p2Link1: "appartement",
      p2Mid: " of een volledig ",
      p2Link2: "huis",
      p2Mid2: " met bijgebouwen omvatten. Sommige ruimtes vergen bijzondere aandacht, met name ",
      p2Link3: "kelders, zolders en garages",
      p2After: ", die vaak meer tijd vragen dan de leefruimtes.",
      p3: "Een huis leegmaken omvat daarentegen geen grote renovatiewerken en geen professionele diepreiniging (vlekken op de vloer, schilderwerk, ontsmetting): het gaat om het afvoeren van de inhoud, niet om het pand als nieuw op te leveren. Als een grondigere reiniging na de afvoer nodig is, gebeurt dit doorgaans als een aparte dienst, te verduidelijken bij de offerte.",
    },
    s2: {
      title: "In welke situaties is een huis leegmaken nuttig?",
      pBefore: "De meest voorkomende aanvraag betreft ",
      pLink: "nalatenschappen",
      pAfter: ", wanneer een woning vrijgemaakt moet worden vóór een verkoop of een verdeling tussen erfgenamen. Maar ook andere situaties komen regelmatig terug:",
      items: [
        "Het te koop of te huur zetten van een pand, om het leeg voor te stellen aan bezoekers of toekomstige huurders",
        "Het vertrek van een naaste naar een rusthuis, wanneer de woning teruggegeven of overgenomen moet worden",
        "Het einde van een huurcontract, met de verplichting om een leeggemaakte woning in goede staat terug te geven",
        "Een opruiming vóór werken of renovatie",
        "Een scheiding of verhuizing waarbij een woning snel leeggemaakt moet worden",
      ],
      p2: "Elke situatie heeft haar eigen tijdsdruk en gevoeligheid — een nalatenschap wordt zelden met dezelfde urgentie behandeld als het einde van een huurcontract. Deze situaties delen echter één gemeenschappelijk punt: het pand moet binnen een bepaalde termijn leeggemaakt worden, met andere belangen naargelang de context. Een nalatenschap vergt vaak een langere sortering, de tijd om voorwerpen met affectieve waarde te identificeren; het einde van een huurcontract legt daarentegen doorgaans een strakkere termijn op, vastgelegd in het huurcontract.",
    },
    s3: {
      title: "Hoe verloopt een huis leegmaken, stap voor stap",
      intro: "Het verloop volgt globaal steeds dezelfde logica, ongeacht de grootte van het pand: een eerste contact, een schatting, een offerte, de interventie, en dan de oplevering van het pand. Hier leest u wat elke stap concreet inhoudt.",
      steps: [
        ["Eerste contact", "U beschrijft uw situatie telefonisch of via het online formulier: type pand, geschat volume, gewenste termijn, eventuele aanwezigheid van waardevolle voorwerpen."],
        ["Schatting", "Wij evalueren het volume, de toegang en de aanwezige voorwerpen, ter plaatse of aan de hand van foto's en een gedetailleerde beschrijving."],
        ["Gratis offerte", "U ontvangt een duidelijke en gedetailleerde offerte, inclusief de aftrek van de ingekochte voorwerpen indien van toepassing, zonder enige verplichting van uw kant."],
        ["Interventie", "Het team ruimt het pand volledig op, op de afgesproken datum, met inachtneming van de vooraf vastgelegde toegangsmodaliteiten."],
        ["Pand vrijgemaakt", "Het pand is leeggemaakt en klaar om overgedragen te worden: verkoop, verhuring, sleuteloverdracht aan de syndicus of de eigenaar."],
      ],
      h3a: "De schatting: ter plaatse of op afstand",
      pa: "Voor een klein volume of een eenvoudige situatie volstaan enkele foto's en een beschrijving vaak om een betrouwbare offerte op te stellen. Voor een groter pand, met meerdere kamers, een kelder en een zolder, laat een bezoek ter plaatse toe om het werkelijke volume en de toegangsbeperkingen nauwkeuriger in te schatten, en zo onaangename verrassingen op de dag van de interventie te vermijden.",
      h3b: "De dag van de interventie",
      pb: "Het team werkt kamer per kamer: de reeds als ingekocht geïdentificeerde voorwerpen worden apart gezet, de rest wordt afgevoerd naar de gepaste kanalen. Voor een studio kan de interventie in één voormiddag afgerond zijn; voor een huis met bijgebouwen kan het één tot twee dagen duren, afhankelijk van het volume en het aantal ingezette medewerkers.",
    },
    s4: {
      title: "Hoeveel tijd en welke prijs moet u voorzien?",
      p1Before: "Er bestaat geen vast tarief: de prijs van een huis leegmaken hangt af van het af te voeren volume, de toegang tot het pand (verdiepingen, lift, parkeergelegenheid) en de aanwezigheid van waardevolle voorwerpen die de factuur verlagen. Onze pagina ",
      p1Link1: "tarieven",
      p1Mid: " beschrijft de factoren die meespelen, en ons artikel over ",
      p1Link2: "de kostprijs van een huis leegmaken in Brussel",
      p1After: " gaat dieper in op dit specifieke onderwerp. In alle gevallen is de offerte gratis en vrijblijvend.",
      p2: "De uiteindelijke prijs hangt voornamelijk af van:",
      items: [
        "Het werkelijke af te voeren volume, kamer per kamer",
        "De toegang tot het pand: verdieping, aanwezigheid van een lift, mogelijkheid om in de buurt te parkeren",
        "Het aantal bijruimtes die naast de woning zelf behandeld moeten worden (kelder, zolder, garage)",
        "De waarde van de ingekochte voorwerpen, die het offertebedrag rechtstreeks verlaagt",
        "De mate van urgentie van de aanvraag, die de organisatie van de interventie kan beïnvloeden",
      ],
      p3Before: "Een “helemaal gratis huis leegmaken” is enkel realistisch in zeer specifieke gevallen, doorgaans wanneer de waarde van de ingekochte voorwerpen de volledige kostprijs van de interventie dekt — wat eerder de uitzondering dan de regel blijft. Ons artikel ",
      p3Link: "is een gratis huis leegmaken mogelijk?",
      p3After: " legt uit in welke gevallen deze situatie zich kan voordoen.",
    },
    ctaBox1: {
      title: "Een idee van het af te voeren volume?",
      text: "Vraag een gratis en vrijblijvende schatting aan, ter plaatse of op afstand naargelang uw situatie.",
      call: "Bel nu",
      quote: "Gratis offerte",
    },
    s5: {
      title: "Sortering, afvoer en valorisatie: wat gebeurt er met de voorwerpen?",
      p1Before: "Vóór elke afvoer bekijken we wat echte waarde heeft: antiek meubilair, juwelen, zilverwerk, schilderijen, verzamelingen. Deze voorwerpen worden voorgesteld voor inkoop, en hun bedrag wordt rechtstreeks van de offerte afgetrokken — zie onze pagina ",
      p1Link1: "inkoop van waardevolle voorwerpen",
      p1Mid: ". Om te leren herkennen wat het waard is om apart te zetten vóór ons bezoek, geeft ons artikel ",
      p1Link2: "hoe een waardevol voorwerp herkennen",
      p1After: " concrete richtlijnen.",
      p2Before: "De rest van de inhoud — meubilair zonder marktwaarde, grofvuil, diverse afval — wordt afgevoerd naar de gepaste sorteer- en recyclagekanalen, met naleving van de geldende regels in Brussel. Meer details over dit traject en wat er werkelijk gerecupereerd of gerecycleerd wordt, vindt u in ons artikel ",
      p2Link: "wat gebeurt er met meubels na een ontruiming",
      p2After: ".",
      p3: "De sortering gebeurt doorgaans in twee fasen: een eerste identificatie van waardevolle voorwerpen tijdens de schatting, gevolgd door een nauwkeurigere controle op de dag van de interventie, kamer per kamer, vóór de afvoer. Deze dubbele controle beperkt het risico dat een interessant voorwerp per vergissing met de rest meegaat.",
    },
    s6: {
      title: "Toegang, logistiek en voorbereiding: wat te anticiperen",
      p1: "In Brussel weegt de toegang tot het pand vaak even zwaar door als het volume bij de organisatie van een huis leegmaken. Een appartement op de vierde verdieping zonder lift in het stadscentrum wordt niet op dezelfde manier behandeld als een gelijkvloers huis met rechtstreekse toegang tot de straat. Enkele punten komen regelmatig terug: de al dan niet aanwezigheid van een lift, de mogelijkheid om in de buurt te parkeren (een veelvoorkomend onderwerp in gemeenten met dichte bebouwing), en de toestemming van de syndicus wanneer het pand deel uitmaakt van een mede-eigendom.",
      p2: "De Brusselse bebouwing kent talrijke configuraties: herenhuizen met meerdere verdiepingen en een gewelfde kelder, appartementsgebouwen met smalle trappenhuizen, aaneengesloten huizen die enkel vanaf de straat bereikbaar zijn. Elk van deze configuraties beïnvloedt rechtstreeks de behandelingstijd en het aantal medewerkers dat ter plaatse nodig is.",
      p3Before: "Kelders, zolders en garages vragen een bijzondere voorbereiding: soms beperkte toegang, voorwerpen die al lang opgeslagen liggen, mogelijke aanwezigheid van zwaardere of omvangrijkere elementen. Ons artikel over de ",
      p3Link: "duur van een ontruiming van kelder of zolder",
      p3After: " beschrijft wat de benodigde tijd voor deze specifieke ruimtes beïnvloedt.",
      p4Before: "Vóór de interventie vergemakkelijken enkele eenvoudige handelingen het verloop van de dag aanzienlijk: te bewaren voorwerpen en persoonlijke documenten apart zetten, eventuele toegangsbeperkingen melden, en een realistisch tijdslot voorzien. Onze ",
      p4Link: "checklist vóór een huis leegmaken",
      p4After: " bevat al deze voorbereidingspunten.",
    },
    s7: {
      title: "Waarom een beroep doen op een gespecialiseerd bedrijf",
      p1: "Een volledig pand zelf leegmaken vergt tijd, toegang tot een geschikt voertuig, en een goede kennis van de afvoer- en recyclagekanalen. Een beroep doen op een gespecialiseerd bedrijf laat toe het volledige proces aan één contactpersoon toe te vertrouwen: schatting, sortering, valorisatie van interessante voorwerpen en afvoer, zonder zelf meerdere dienstverleners te moeten coördineren.",
      p2Before: "Het is ook een manier om een objectieve inschatting te krijgen van de aanwezige voorwerpen vóór ze afgevoerd worden, in plaats van zelf te moeten beslissen wat waarde heeft en wat niet. U kunt concrete voorbeelden van interventies bekijken op onze pagina ",
      p2Link1: "realisaties",
      p2Mid: " en de reacties van klanten op onze pagina ",
      p2Link2: "beoordelingen",
      p2After: ".",
      p3: "Dit ontslaat u niet van waakzaamheid: vraag altijd een schriftelijke offerte vóór de interventie, verduidelijk wat inbegrepen is (enkel afvoer of ook een basisreiniging), en aarzel niet om vragen te stellen over het afvoerkanaal dat voor het afval gebruikt wordt. Een ernstig bedrijf beantwoordt deze vragen duidelijk vóór de grote dag.",
    },
    retenir: {
      title: "Wat u moet onthouden",
      items: [
        "Een huis leegmaken omvat het volledige pand, met sortering en valorisatie van voorwerpen — meer dan een eenvoudige ontruiming.",
        "De prijs hangt af van het volume, de toegang en de ingekochte voorwerpen: er is geen vast tarief, maar de offerte is altijd gratis.",
        "De sortering maakt onderscheid tussen wat ingekocht wordt, wat afgevoerd wordt en wat u bewaart; u wordt steeds geïnformeerd vóór elk inkoopvoorstel.",
        "De toegang (verdiepingen, lift, parkeren, toestemming van de syndicus) beïnvloedt de organisatie even sterk als het af te voeren volume.",
        "Persoonlijke documenten en voorwerpen met affectieve waarde zet u zelf best apart vóór de interventie.",
      ],
    },
    s8: {
      title: "Brussel, gemeente per gemeente",
      pBefore: "Elke Brusselse gemeente heeft haar eigenheden — type bebouwing, al dan niet aanwezigheid van liften, parkeerregels, dichtheid van de buurt — die de organisatie van een huis leegmaken beïnvloeden. Vind de informatie voor uw sector op onze pagina ",
      pLink1: "huis leegmaken in Brussel, gemeente per gemeente",
      pMid: ". We hebben ook bepaalde lokale situaties uitgewerkt in specifieke gidsen, bijvoorbeeld voor ",
      pLink2: "Elsene",
      pSep1: ", ",
      pLink3: "Ukkel",
      pSep2: ", ",
      pLink4: "Schaarbeek",
      pMid2: " of ",
      pLink5: "Anderlecht",
      pAfter: ".",
    },
    faqTitle: "Veelgestelde vragen",
    faq: [
      {
        q: "Hoelang duurt een huis leegmaken in Brussel?",
        a: "Dit hangt vooral af van het volume en de configuratie van het pand: een studio of een appartement met één slaapkamer wordt doorgaans in enkele uren leeggemaakt, terwijl een huis met kelder, zolder en tuin één tot twee volledige dagen kan vergen. De toegang (verdiepingen, lift, parkeren) en de aanwezigheid van voorwerpen die zorgvuldig gesorteerd moeten worden, bijvoorbeeld bij een nalatenschap, kunnen de interventie ook verlengen. De benodigde tijd wordt met de offerte geschat, vóór elke interventie.",
      },
      {
        q: "Moet ik aanwezig zijn op de dag van de interventie?",
        a: "Dit is geen verplichting als de toegangsmodaliteiten (sleutels, code, aanwezigheid van een syndicus) vooraf zijn afgesproken en als de te bewaren voorwerpen al apart zijn gezet. Aanwezig zijn, al was het maar aan het begin, laat echter toe om samen te bevestigen wat vertrekt en wat blijft, en om vragen te beantwoorden die tijdens de dag kunnen opduiken.",
      },
      {
        q: "Is de inkoop van waardevolle voorwerpen gegarandeerd?",
        a: "Nee: enkel voorwerpen met een echte marktwaarde — antiek meubilair, juwelen, zilverwerk, kunstwerken, bepaalde verzamelingen — komen in aanmerking voor een inkoopvoorstel. Dit is nooit systematisch en hangt volledig af van wat zich in het pand bevindt. U wordt steeds geïnformeerd vóór elk voorstel en bent vrij om het te aanvaarden of te weigeren; het bedrag, indien van toepassing, wordt van de eindofferte afgetrokken.",
      },
      {
        q: "Wat te doen met papieren en persoonlijke documenten vóór de komst van het team?",
        a: "Het wordt aanbevolen om zelf administratieve, financiële of affectieve documenten (foto's, brieven, notitieboekjes) te verzamelen vóór de interventie, aangezien een ontruimingsteam niet in staat is om het belang ervan te beoordelen. Bij een nalatenschap voorkomt deze voorafgaande sortering ook het verlies van een document dat nuttig is voor de notariële formaliteiten.",
      },
      {
        q: "Is een huis leegmaken mogelijk zonder parkeerplaats vlak voor het gebouw?",
        a: "Ja, maar het is beter dit meteen bij de offerteaanvraag te melden: het team kan zich dan dienovereenkomstig organiseren, bijvoorbeeld met een iets verder gelegen parkeerplaats of een tijdslot aangepast aan de zone. In gemeenten met dichte bebouwing in het centrum van Brussel komt dit punt vaak voor en wordt het eenvoudig vooraf voorbereid.",
      },
      {
        q: "Wat is het verschil tussen een huis leegmaken en een klassieke ontruiming?",
        a: "Een ontruiming beperkt zich vaak tot de afvoer van een ruimte of een eenmalige partij grofvuil. Een huis leegmaken omvat het volledige pand — leefruimtes, kelder, zolder, garage — met een sorterings- en valorisatiestap voor interessante voorwerpen, met als doel de woning volledig vrij te maken, bijvoorbeeld voor een verkoop of een sleuteloverdracht.",
      },
      {
        q: "Kan ik een gedeeltelijk huis leegmaken vragen, bijvoorbeeld enkel een kelder of een zolder?",
        a: "Ja, een interventie kan beperkt worden tot één ruimte als dat uw werkelijke behoefte is — een kelder te ledigen vóór werken, een zolder vóór de verkoop van een pand. Het principe blijft hetzelfde (schatting, offerte, sortering, afvoer), enkel het betrokken volume verandert, wat rechtstreeks in de eindprijs tot uiting komt.",
      },
    ],
    ctaBox2: {
      title: "Klaar om uw offerte aan te vragen?",
      text: "Contacteer ons voor een gratis en vrijblijvende schatting.",
      call: "Bel nu",
      quote: "Gratis offerte",
    },
  },
  en: {
    badge: "Guide",
    h1: "House clearance in Brussels: the complete guide to organising your project",
    heroSubtitle:
      "Whether you're preparing for an estate, a sale, or simply need to clear the clutter, here's how a house clearance unfolds in Brussels, from the first estimate to the handover of the keys.",
    ctaCall: "Call now",
    ctaQuote: "Request a free quote",
    intro:
      "A house clearance in Brussels means fully removing the contents of a property — furniture, bulky items, cellar, attic, garage — before a sale, a rental, or as part of an estate settlement. Unlike a one-off clear-out, it generally includes sorting the items, valuing anything with real market worth, and handing back the property clean and completely empty. This guide details the concrete steps of a house clearance, the factors that influence price and timing, how items are sorted and removed, and the access and preparation points to anticipate depending on your commune. You'll also find answers to the questions Brussels residents ask most often before calling in a specialised company.",
    s1: {
      title: "What exactly is a house clearance?",
      p1: "A house clearance is a broader operation than a simple clear-out: it's not just about removing bulky items, but about handling the entire property, room by room, distinguishing what should be kept, what can be bought back, and what goes for removal. The end goal is to leave the property completely empty — not necessarily renovated or thoroughly cleaned, but stripped of all contents and ready for the next step: a sale, a rental, or handing the keys back to the managing agent or owner.",
      p2Before: "Depending on the property's layout, a house clearance can be limited to an ",
      p2Link1: "apartment",
      p2Mid: " or cover a ",
      p2Link2: "whole house",
      p2Mid2: " with its outbuildings. Certain spaces need particular attention, notably ",
      p2Link3: "cellars, attics and garages",
      p2After: ", which often take longer to clear than the living areas.",
      p3: "A house clearance does not, however, include major renovation work or deep professional cleaning (floor marks, painting, disinfection): it's about removing the contents, not restoring the property to a like-new condition. If more thorough cleaning is needed after the removal, this is usually a separate service, to be clarified in the quote.",
    },
    s2: {
      title: "When is a house clearance useful?",
      pBefore: "The most common request concerns ",
      pLink: "estate clearances",
      pAfter: ", when a home needs to be vacated before a sale or a division between heirs. But other situations come up regularly too:",
      items: [
        "Putting a property up for sale or rent, to present it empty to visitors or prospective tenants",
        "A loved one moving into a care home, when the home needs to be handed back or taken over",
        "The end of a lease, with the obligation to return an emptied property in good condition",
        "Decluttering before renovation work",
        "A separation or a move that requires quickly emptying a home",
      ],
      p2: "Each situation has its own timing constraints and sensitivities — an estate clearance is rarely handled with the same urgency as the end of a lease. These situations do share one thing in common though: the property must be cleared within a set timeframe, with different stakes depending on the context. An estate clearance often involves a longer sorting process, taking the time to identify items of sentimental value; the end of a lease, on the other hand, generally imposes a tighter deadline, set by the rental agreement.",
    },
    s3: {
      title: "How a house clearance unfolds, step by step",
      intro: "The process broadly follows the same logic regardless of the property's size: an initial contact, an estimate, a quote, the intervention, then the handover of the property. Here's what each step involves in practice.",
      steps: [
        ["Initial contact", "You describe your situation by phone or via the online form: type of property, approximate volume, desired timeframe, any items of value present."],
        ["Estimate", "We assess the volume, the access and the items present, either on site or from photos and a detailed description."],
        ["Free quote", "You receive a clear, detailed quote, including the deduction for any bought-back items where applicable, with no obligation on your part."],
        ["Intervention", "The team removes the property's contents entirely, on the agreed date, in line with the access arrangements set beforehand."],
        ["Property cleared", "The property is emptied and ready to be handed over: sale, rental, or keys returned to the managing agent or owner."],
      ],
      h3a: "The estimate: on site or remote",
      pa: "For a small volume or a simple situation, a few photos and a description are often enough to draw up a reliable quote. For a larger property, with several rooms, a cellar and an attic, an on-site visit allows for a more precise assessment of the actual volume and any access constraints, avoiding unpleasant surprises on the day of the intervention.",
      h3b: "The day of the intervention",
      pb: "The team works room by room: items already identified for buyback are set aside, the rest is removed to the appropriate channels. For a studio, the intervention can be finished in a morning; for a house with outbuildings, it can span one to two days depending on the volume and the number of people involved.",
    },
    s4: {
      title: "How much time and what price should you expect?",
      p1Before: "There's no fixed rate: the price of a house clearance depends on the volume to be removed, access to the property (floors, lift, parking) and the presence of valuable items that reduce the bill. Our ",
      p1Link1: "prices",
      p1Mid: " page details the factors involved, and our article on ",
      p1Link2: "the cost of a house clearance in Brussels",
      p1After: " goes further into this specific topic. In all cases, the quote is free and comes with no obligation.",
      p2: "The final price mainly depends on:",
      items: [
        "The actual volume to be removed, room by room",
        "Access to the property: floor level, presence of a lift, ability to park nearby",
        "The number of ancillary spaces to be dealt with (cellar, attic, garage) in addition to the home itself",
        "The value of any bought-back items, which directly reduces the quoted amount",
        "The degree of urgency of the request, which can affect how the intervention is organised",
      ],
      p3Before: "A “completely free house clearance” is only realistic in very specific cases, generally when the value of the bought-back items covers the entire cost of the intervention — which remains the exception rather than the rule. Our article ",
      p3Link: "is a free house clearance possible?",
      p3After: " explains in which cases this situation can arise.",
    },
    ctaBox1: {
      title: "Have an idea of the volume to remove?",
      text: "Request a free, no-obligation estimate, on site or remotely depending on your situation.",
      call: "Call now",
      quote: "Free quote",
    },
    s5: {
      title: "Sorting, removal and valuation: what happens to the items?",
      p1Before: "Before any removal, we examine what has real value: antique furniture, jewellery, silverware, paintings, collections. These items are offered for buyback, and their amount is deducted directly from the quote — see our ",
      p1Link1: "buyback of valuable items",
      p1Mid: " page. To learn how to recognise what's worth setting aside before our visit, our article ",
      p1Link2: "how to recognise a valuable item",
      p1After: " gives concrete pointers.",
      p2Before: "The rest of the contents — furniture with no market value, bulky items, various waste — is removed to the appropriate sorting and recycling channels, in line with the rules in force in Brussels. You'll find more detail on this process and what is actually recovered or recycled in our article ",
      p2Link: "what happens to furniture after a clearance",
      p2After: ".",
      p3: "Sorting generally happens in two stages: an initial identification of valuable items during the estimate, then a finer check on the day of the intervention, room by room, before removal. This double-check limits the risk of an interesting item being mistakenly removed with the rest.",
    },
    s6: {
      title: "Access, logistics and preparation: what to anticipate",
      p1: "In Brussels, access to the property often weighs as heavily as the volume in organising a house clearance. A fourth-floor apartment with no lift in the city centre isn't handled the same way as a ground-floor house with direct street access. A few points come up regularly: whether or not there's a lift, the ability to park nearby (a common issue in densely built communes), and the managing agent's agreement when the property is part of a co-ownership.",
      p2: "Brussels' building stock covers a wide range of configurations: townhouses with several floors and a vaulted cellar, apartment buildings with narrow staircases, terraced houses accessible only from the street. Each of these configurations directly affects the handling time and the number of people needed on site.",
      p3Before: "Cellars, attics and garages require particular preparation: sometimes restricted access, items stored for a long time, the possible presence of heavier or bulkier elements. Our article on the ",
      p3Link: "duration of a cellar or attic clearance",
      p3After: " details what influences the time needed for these specific spaces.",
      p4Before: "Before the intervention, a few simple steps make the day run noticeably more smoothly: setting aside items to keep and personal documents, flagging any access restrictions, and planning a realistic time slot. Our ",
      p4Link: "checklist before a house clearance",
      p4After: " covers all these preparation points.",
    },
    s7: {
      title: "Why call on a specialised company",
      p1: "Clearing an entire property yourself takes time, access to a suitable vehicle, and good knowledge of removal and recycling channels. Calling on a specialised company allows you to hand over the whole process to a single point of contact: estimate, sorting, valuation of interesting items and removal, without having to coordinate several providers yourself.",
      p2Before: "It's also a way to get an objective assessment of the items present before they're removed, rather than having to decide alone what has value and what doesn't. You can see concrete examples of past interventions on our ",
      p2Link1: "past work",
      p2Mid: " page and customer feedback on our ",
      p2Link2: "reviews",
      p2After: " page.",
      p3: "This doesn't mean you shouldn't stay alert: always ask for a written quote before the intervention, clarify what's included (removal only, or also a basic clean), and don't hesitate to ask about the removal channel used for waste. A serious company will answer these questions clearly before the day itself.",
    },
    retenir: {
      title: "Key takeaways",
      items: [
        "A house clearance covers the whole property, with sorting and valuation of items — going beyond a simple clear-out.",
        "The price depends on the volume, access and any bought-back items: there's no fixed rate, but the quote is always free.",
        "Sorting distinguishes what's bought back, what's removed and what you keep; you're always informed before any buyback offer.",
        "Access (floors, lift, parking, managing agent's agreement) affects the organisation just as much as the volume to be removed.",
        "Personal documents and sentimental items should be set aside by you before the intervention.",
      ],
    },
    s8: {
      title: "Brussels, commune by commune",
      pBefore: "Every Brussels commune has its own characteristics — type of building stock, presence or absence of lifts, parking rules, neighbourhood density — that affect how a house clearance is organised. Find the information specific to your area on our ",
      pLink1: "house clearance in Brussels, commune by commune",
      pMid: " page. We've also detailed certain local situations in dedicated guides, for example for ",
      pLink2: "Ixelles",
      pSep1: ", ",
      pLink3: "Uccle",
      pSep2: ", ",
      pLink4: "Schaerbeek",
      pMid2: " or ",
      pLink5: "Anderlecht",
      pAfter: ".",
    },
    faqTitle: "Frequently asked questions",
    faq: [
      {
        q: "How long does a house clearance take in Brussels?",
        a: "This mainly depends on the volume and layout of the property: a studio or one-bedroom apartment is usually cleared in a few hours, while a house with a cellar, attic and garden can take one to two full days. Access (floors, lift, parking) and the presence of items requiring careful sorting, for example during an estate clearance, can also extend the intervention. The time required is estimated with the quote, before any intervention takes place.",
      },
      {
        q: "Do I need to be present on the day of the intervention?",
        a: "This isn't a requirement if access arrangements (keys, code, presence of a managing agent) have been agreed in advance and the items to be kept have already been set aside. That said, being present, even just at the start, allows you to jointly confirm what's going and what's staying, and to answer any questions that come up during the day.",
      },
      {
        q: "Is the buyback of valuable items guaranteed?",
        a: "No: only items with real market value — antique furniture, jewellery, silverware, artworks, certain collections — are offered a buyback. This is never systematic and depends entirely on what's found in the property. You're always informed before any offer and free to accept or decline it; the amount, if any, is deducted from the final quote.",
      },
      {
        q: "What should I do with papers and personal documents before the team arrives?",
        a: "We recommend gathering administrative, financial or sentimental documents (photos, letters, notebooks) yourself before the intervention, as a clearance team isn't in a position to judge their importance. In the case of an estate clearance, this preliminary sorting also avoids losing a document useful for notarial procedures.",
      },
      {
        q: "Is a house clearance possible without a parking spot right outside the building?",
        a: "Yes, but it's best to mention it as soon as you request a quote: the team can organise accordingly, for example with parking slightly further away or a time slot adapted to the area. In the densely built communes of central Brussels, this comes up often and is simply prepared for in advance.",
      },
      {
        q: "What's the difference between a house clearance and a standard clear-out?",
        a: "A clear-out is often limited to removing a space or a one-off batch of bulky items. A house clearance covers the entire property — living areas, cellar, attic, garage — with a sorting and valuation step for interesting items, aiming to leave the home completely empty, for example ahead of a sale or a key handover.",
      },
      {
        q: "Can I request a partial house clearance, for example just a cellar or an attic?",
        a: "Yes, an intervention can be limited to a single space if that's what you actually need — a cellar to be cleared before renovation work, an attic before selling a property. The principle stays the same (estimate, quote, sorting, removal); only the volume involved changes, which is directly reflected in the final price.",
      },
    ],
    ctaBox2: {
      title: "Ready to get your quote?",
      text: "Contact us for a free, no-obligation estimate.",
      call: "Call now",
      quote: "Free quote",
    },
  },
};

export default function PageClient({ locale }) {
  const c = CONTENT[locale];

  return (
    <article>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: c.faq.map((item) => ({
              "@type": "Question",
              name: item.q,
              acceptedAnswer: { "@type": "Answer", text: item.a },
            })),
          }),
        }}
      />

      <section className="relative overflow-hidden bg-sand text-ink">
        <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(900px 480px at 15% -10%, rgba(27,75,51,0.06), transparent 55%)" }} />
        <div className="relative mx-auto max-w-[900px] px-6 py-20 lg:px-10 lg:py-24">
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-accent">{c.badge}</p>
          <h1 className="mt-3 max-w-3xl font-serif text-4xl font-black leading-[1.1] md:text-5xl">
            {c.h1}
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-ink/70 leading-relaxed">
            {c.heroSubtitle}
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a href={`tel:${PHONE_TEL}`} className="inline-flex items-center gap-2 rounded-2xl bg-cta-700 px-7 py-3.5 font-semibold text-white shadow-cta transition hover:-translate-y-0.5 hover:bg-cta-800">
              <PhoneIcon className="h-5 w-5" />
              {c.ctaCall}
            </a>
            <a href="#contactus" className="inline-flex items-center gap-2 rounded-2xl border-2 border-ink/15 bg-white px-7 py-3.5 font-semibold text-ink transition hover:border-accent hover:text-accent">
              {c.ctaQuote}
              <ArrowRightIcon className="h-5 w-5" />
            </a>
          </div>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="mx-auto max-w-[800px] px-6 lg:px-10 space-y-8">
          <div>
            <p className="leading-relaxed text-neutral-700">{c.intro}</p>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">{c.s1.title}</h2>
            <p className="mt-3 leading-relaxed text-neutral-700">{c.s1.p1}</p>
            <p className="mt-3 leading-relaxed text-neutral-700">
              {c.s1.p2Before}
              <Link href="/debarras-appartement" className="font-semibold text-accent underline underline-offset-4">
                {c.s1.p2Link1}
              </Link>
              {c.s1.p2Mid}
              <Link href="/debarras-maison" className="font-semibold text-accent underline underline-offset-4">
                {c.s1.p2Link2}
              </Link>
              {c.s1.p2Mid2}
              <Link href="/debarras-cave-grenier-garage" className="font-semibold text-accent underline underline-offset-4">
                {c.s1.p2Link3}
              </Link>
              {c.s1.p2After}
            </p>
            <p className="mt-3 leading-relaxed text-neutral-700">{c.s1.p3}</p>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">{c.s2.title}</h2>
            <p className="mt-3 leading-relaxed text-neutral-700">
              {c.s2.pBefore}
              <Link href="/blog/vide-maison-succession-bruxelles" className="font-semibold text-accent underline underline-offset-4">
                {c.s2.pLink}
              </Link>
              {c.s2.pAfter}
            </p>
            <ul className="mt-4 space-y-2">
              {c.s2.items.map((item) => (
                <li key={item} className="flex items-start gap-2 text-neutral-700">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                  {item}
                </li>
              ))}
            </ul>
            <p className="mt-3 leading-relaxed text-neutral-700">{c.s2.p2}</p>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">{c.s3.title}</h2>
            <p className="mt-3 leading-relaxed text-neutral-700">{c.s3.intro}</p>
            <ol className="mt-4 space-y-4">
              {c.s3.steps.map(([title, text], i) => (
                <li key={title} className="flex gap-4 rounded-2xl border border-sand-line bg-sand p-5">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-ink font-serif font-bold text-white">
                    {i + 1}
                  </span>
                  <div>
                    <p className="font-bold text-ink">{title}</p>
                    <p className="mt-1 text-sm text-neutral-600">{text}</p>
                  </div>
                </li>
              ))}
            </ol>

            <h3 className="mt-6 font-serif text-xl font-bold text-ink">{c.s3.h3a}</h3>
            <p className="mt-3 leading-relaxed text-neutral-700">{c.s3.pa}</p>

            <h3 className="mt-6 font-serif text-xl font-bold text-ink">{c.s3.h3b}</h3>
            <p className="mt-3 leading-relaxed text-neutral-700">{c.s3.pb}</p>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">{c.s4.title}</h2>
            <p className="mt-3 leading-relaxed text-neutral-700">
              {c.s4.p1Before}
              <Link href="/tarifs" className="font-semibold text-accent underline underline-offset-4">
                {c.s4.p1Link1}
              </Link>
              {c.s4.p1Mid}
              <Link href="/blog/combien-coute-vide-maison-bruxelles" className="font-semibold text-accent underline underline-offset-4">
                {c.s4.p1Link2}
              </Link>
              {c.s4.p1After}
            </p>
            <p className="mt-3 leading-relaxed text-neutral-700">{c.s4.p2}</p>
            <ul className="mt-4 space-y-2">
              {c.s4.items.map((item) => (
                <li key={item} className="flex items-start gap-2 text-neutral-700">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                  {item}
                </li>
              ))}
            </ul>
            <p className="mt-3 leading-relaxed text-neutral-700">
              {c.s4.p3Before}
              <Link href="/blog/vide-maison-gratuit-est-ce-possible" className="font-semibold text-accent underline underline-offset-4">
                {c.s4.p3Link}
              </Link>
              {c.s4.p3After}
            </p>
          </div>

          <div className="rounded-3xl border border-sand-line bg-ink p-8 text-white">
            <h2 className="font-serif text-2xl font-extrabold">{c.ctaBox1.title}</h2>
            <p className="mt-2 text-white/75">{c.ctaBox1.text}</p>
            <div className="mt-6 flex flex-wrap gap-4">
              <a href={`tel:${PHONE_TEL}`} className="inline-flex items-center gap-2 rounded-2xl bg-cta-700 px-6 py-3 font-semibold text-white shadow-cta transition hover:-translate-y-0.5 hover:bg-cta-800">
                <PhoneIcon className="h-5 w-5" />
                {c.ctaBox1.call}
              </a>
              <a href="#contactus" className="inline-flex items-center gap-2 rounded-2xl border border-white/25 bg-white/10 px-6 py-3 font-semibold text-white transition hover:bg-white/20">
                <CheckCircleIcon className="h-5 w-5" />
                {c.ctaBox1.quote}
              </a>
            </div>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">{c.s5.title}</h2>
            <p className="mt-3 leading-relaxed text-neutral-700">
              {c.s5.p1Before}
              <Link href="/rachat-objets" className="font-semibold text-accent underline underline-offset-4">
                {c.s5.p1Link1}
              </Link>
              {c.s5.p1Mid}
              <Link href="/blog/reconnaitre-objet-valeur-rachat" className="font-semibold text-accent underline underline-offset-4">
                {c.s5.p1Link2}
              </Link>
              {c.s5.p1After}
            </p>
            <p className="mt-3 leading-relaxed text-neutral-700">
              {c.s5.p2Before}
              <Link href="/blog/que-deviennent-meubles-apres-debarras" className="font-semibold text-accent underline underline-offset-4">
                {c.s5.p2Link}
              </Link>
              {c.s5.p2After}
            </p>
            <p className="mt-3 leading-relaxed text-neutral-700">{c.s5.p3}</p>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">{c.s6.title}</h2>
            <p className="mt-3 leading-relaxed text-neutral-700">{c.s6.p1}</p>
            <p className="mt-3 leading-relaxed text-neutral-700">{c.s6.p2}</p>
            <p className="mt-3 leading-relaxed text-neutral-700">
              {c.s6.p3Before}
              <Link href="/blog/duree-debarras-cave-grenier" className="font-semibold text-accent underline underline-offset-4">
                {c.s6.p3Link}
              </Link>
              {c.s6.p3After}
            </p>
            <p className="mt-3 leading-relaxed text-neutral-700">
              {c.s6.p4Before}
              <Link href="/blog/checklist-avant-vide-maison" className="font-semibold text-accent underline underline-offset-4">
                {c.s6.p4Link}
              </Link>
              {c.s6.p4After}
            </p>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">{c.s7.title}</h2>
            <p className="mt-3 leading-relaxed text-neutral-700">{c.s7.p1}</p>
            <p className="mt-3 leading-relaxed text-neutral-700">
              {c.s7.p2Before}
              <Link href="/realisations" className="font-semibold text-accent underline underline-offset-4">
                {c.s7.p2Link1}
              </Link>
              {c.s7.p2Mid}
              <Link href="/avis" className="font-semibold text-accent underline underline-offset-4">
                {c.s7.p2Link2}
              </Link>
              {c.s7.p2After}
            </p>
            <p className="mt-3 leading-relaxed text-neutral-700">{c.s7.p3}</p>
          </div>

          <div className="rounded-2xl border border-sand-line bg-sand p-6">
            <h2 className="font-serif text-xl font-extrabold text-ink">{c.retenir.title}</h2>
            <ul className="mt-4 space-y-2">
              {c.retenir.items.map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm text-neutral-700">
                  <CheckCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">{c.s8.title}</h2>
            <p className="mt-3 leading-relaxed text-neutral-700">
              {c.s8.pBefore}
              <Link href="/vide-maison" className="font-semibold text-accent underline underline-offset-4">
                {c.s8.pLink1}
              </Link>
              {c.s8.pMid}
              <Link href="/blog/vide-maison-ixelles-guide" className="font-semibold text-accent underline underline-offset-4">
                {c.s8.pLink2}
              </Link>
              {c.s8.pSep1}
              <Link href="/blog/vide-maison-uccle-guide" className="font-semibold text-accent underline underline-offset-4">
                {c.s8.pLink3}
              </Link>
              {c.s8.pSep2}
              <Link href="/blog/vide-maison-schaerbeek-guide" className="font-semibold text-accent underline underline-offset-4">
                {c.s8.pLink4}
              </Link>
              {c.s8.pMid2}
              <Link href="/blog/vide-maison-anderlecht-guide" className="font-semibold text-accent underline underline-offset-4">
                {c.s8.pLink5}
              </Link>
              {c.s8.pAfter}
            </p>
          </div>

          <div>
            <h2 className="font-serif text-2xl font-extrabold text-brand-900">{c.faqTitle}</h2>
            <div className="mt-4 space-y-4">
              {c.faq.map((item) => (
                <div key={item.q} className="rounded-2xl border border-sand-line bg-sand p-5">
                  <p className="font-bold text-ink">{item.q}</p>
                  <p className="mt-2 text-sm leading-relaxed text-neutral-600">{item.a}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-3xl border border-sand-line bg-ink p-8 text-white">
            <h2 className="font-serif text-2xl font-extrabold">{c.ctaBox2.title}</h2>
            <p className="mt-2 text-white/75">{c.ctaBox2.text}</p>
            <div className="mt-6 flex flex-wrap gap-4">
              <a href={`tel:${PHONE_TEL}`} className="inline-flex items-center gap-2 rounded-2xl bg-cta-700 px-6 py-3 font-semibold text-white shadow-cta transition hover:-translate-y-0.5 hover:bg-cta-800">
                <PhoneIcon className="h-5 w-5" />
                {c.ctaBox2.call}
              </a>
              <a href="#contactus" className="inline-flex items-center gap-2 rounded-2xl border border-white/25 bg-white/10 px-6 py-3 font-semibold text-white transition hover:bg-white/20">
                <CheckCircleIcon className="h-5 w-5" />
                {c.ctaBox2.quote}
              </a>
            </div>
          </div>
        </div>
      </section>

      <Contact />
    </article>
  );
}
